import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import type {
  Snapshot,
  SnapshotBody,
  SnapshotSource,
} from '@superfunnel/types/snapshot';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { dynamicPageVariantsColRef } from './DynamicPageVariantsService';

export const parseWebsiteEntityPath = (entityPath: string) => {
  const match = entityPath.match(/^campaigns\/([^/]+)\/websites\/([^/]+)$/);
  return match ? { dynamicPageId: match[1], websiteId: match[2] } : undefined;
};

export const findWebsiteVariantId = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
) => {
  const variants = await getDocs(
    query(
      dynamicPageVariantsColRef(accountId, dynamicPageId),
      where('websiteIds', 'array-contains', websiteId),
      limit(1),
    ),
  );
  return variants.docs[0]?.id;
};

export const entitySnapshotsQuery = (
  accountId: string,
  entityPath: string,
  maxCount: number,
) =>
  query(
    collection(db, 'accounts', accountId, 'snapshots').withConverter(
      emptyConverter<Snapshot>(),
    ),
    where('entityPaths', 'array-contains', entityPath),
    orderBy('createdAt', 'desc'),
    limit(maxCount),
  );

interface CreateSnapshotOptions {
  name?: string;
  restoredSnapshotId?: string;
}

export const createSnapshot = async (
  accountId: string,
  entityPaths: string[],
  source: SnapshotSource,
  options: CreateSnapshotOptions = {},
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      entityPaths: string[];
      source: SnapshotSource;
    } & CreateSnapshotOptions,
    { snapshotId: string | null }
  >(functions, 'accounts/snapshots/createSnapshot');
  const res = await fn({ accountId, entityPaths, source, ...options });
  return res.data;
};

export const getSnapshotBody = async (
  accountId: string,
  snapshotId: string,
) => {
  const fn = httpsCallable<
    { accountId: string; snapshotId: string },
    SnapshotBody
  >(functions, 'accounts/snapshots/getSnapshotBody');
  const res = await fn({ accountId, snapshotId });
  return res.data;
};

export const renameSnapshot = (
  accountId: string,
  snapshotId: string,
  name: string,
) =>
  updateDoc(doc(db, 'accounts', accountId, 'snapshots', snapshotId), { name });
